import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import React, {useState} from "react";
import {useNavigate} from "react-router-dom-v5-compat";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {semanticColor, spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    Body,
    HeadingSmall,
    LabelMedium,
} from "@khanacademy/wonder-blocks-typography";
import {usePublishAnalyticsEvent} from "@khan/analytics-learning-time";
import {isDateOverAgeGate} from "@khan/auth-shared";
import {BirthdayPicker} from "@khan/component-birthday-picker";
import {RequestInfo} from "@khan/shared-core";
import {getContinueUrl, parseUrl, useLocation} from "@khan/urls";

import {BackLink} from "./back-link.tsx";
import sharedStyles from "./shared-styles.ts";

interface Props {
    // The text for the back link
    backlinkText?: string;
    // The path for the back link
    backlinkPath?: string;
    // Hide the back link or not
    hideBackLink?: boolean;
    // The max width of the component
    maxWidth?: string;
    // Prefix the next url path. Must leave off the trailing "/".
    // e.g. /writing-coach, not /writing-coach/
    nextUrlPathPrefix?: string;
    /**
     * The class code to use for sign up.
     */
    classCode?: string;
}

/**
 * A form for the user to enter their birthday.
 *
 * This module contains the form, back link, header, etc.
 */
export const LearnerBirthdayForm = ({
    backlinkText = "Choose a different role",
    backlinkPath = "/?skipRole=false",
    maxWidth = "100%",
    hideBackLink = false,
    nextUrlPathPrefix = "",
    classCode,
}: Props): React.ReactNode => {
    const [birthday, setBirthday] = useState("");
    const [validationError, setValidationError] = useState<string>("");
    const navigate = useNavigate();
    const location = useLocation();
    const publishAnalyticsEvent = usePublishAnalyticsEvent();

    React.useEffect(() => {
        if (birthday) {
            publishAnalyticsEvent({
                eventType: "SignupBirthdayFilled",
                eventSchemaVersion: 1,
                isIOS: RequestInfo.isIOS,
            });
        }
        // NOTE(davemcalpine): We only care about tracking changes to the birthday.
        // eslint-disable-next-line react-hooks/exhaustive-deps -- see above
    }, [birthday]);

    const finalRedirectUrl = getContinueUrl(location) ?? "/";

    const backUrl = parseUrl(backlinkPath);
    if (finalRedirectUrl && finalRedirectUrl !== "/") {
        backUrl.searchParams.set("continue", finalRedirectUrl);
    }

    // Determine whether the user will be sent to the over or under age gate
    const onNext = () => {
        if (!birthday) {
            setValidationError("Please enter your birthdate");
            return;
        }

        // Set the user's birthdate on session storage
        sessionStorage.setItem("new_signup_birthday", birthday);

        const userIsUnderAge = !isDateOverAgeGate(birthday);

        const continueUrl = userIsUnderAge
            ? parseUrl(`${nextUrlPathPrefix}/signup/learner/under-age-gate`)
            : parseUrl(`${nextUrlPathPrefix}/signup/learner/over-age-gate`);

        if (classCode) {
            continueUrl.searchParams.set("classcode", classCode);
        }

        if (finalRedirectUrl && finalRedirectUrl !== "/") {
            continueUrl.searchParams.set("continue", finalRedirectUrl);
        }
        // Push to the next page
        navigate(continueUrl.pathname + continueUrl.search);
    };

    return (
        <View style={{maxWidth}}>
            {!hideBackLink && (
                <>
                    <BackLink
                        text={backlinkText}
                        backUrl={backUrl.pathname + backUrl.search}
                        style={styles.backlink}
                    />
                    <Strut size={spacing.medium_16} />
                </>
            )}

            <HeadingSmall>{t`Sign up as a learner today!`}</HeadingSmall>
            <Strut size={spacing.small_12} />
            <LabelMedium style={styles.description}>
                {t`First, we need your date of birth to help us give you the best experience!`}
            </LabelMedium>
            <Strut size={spacing.xLarge_32} />
            <BirthdayPicker
                label={t`Date of birth`}
                required={true}
                defaultValue={birthday}
                onChange={(birthday) => setBirthday(birthday || "")}
                dropdownStyle={styles.birthdayPicker}
            />
            <Strut size={spacing.large_24} />
            <Button onClick={onNext}>{t`Next`}</Button>
            {!!validationError && (
                <Body
                    tag="p"
                    role="alert"
                    style={sharedStyles.fieldError}
                    aria-live="polite"
                >
                    {validationError}
                </Body>
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    backlink: {
        alignSelf: "flex-start",
    },

    description: {
        color: semanticColor.core.foreground.neutral.subtle,
    },
    birthdayPicker: {
        flex: 1,
    },
});
